import {
  CMS_TREATMENT_TYPES,
  CMS_SUPPORTING_SERVICES,
  CMS_CONDITIONS,
  CMS_PATIENT_GROUPS,
  CMS_CONSULTATION_TYPES,
  CMS_ACCESSIBILITY,
  CMS_EMPLOYEE_BENEFITS,
  CMS_FINANCING_OPTIONS,
  CMS_CLINIC_TYPES,
  type ClinicsFilters,
  type Treatment,
  type Service,
  type Condition,
  type PatientGroup,
  type ConsultationType,
  type Accessibility,
  type EmployeeBenefit,
  type Financier,
} from "../clinics.types";
import type { Language } from "@src/packages/types/common";

/**
 * Map language to CMS format (currently just a pass-through)
 */
function mapLanguageToCms(lang: Language): string {
  return lang;
}

/**
 * Helper to add array filter params (handles single vs multiple values)
 */
function addArrayFilterParam(
  params: URLSearchParams,
  field: string,
  values: string[],
  validValues: readonly string[],
): void {
  const filtered = values.filter((v) => validValues.includes(v));
  const unique = Array.from(new Set(filtered));

  if (unique.length === 0) return;

  if (unique.length === 1) {
    params.append(`where[${field}][contains]`, unique[0]!);
  } else {
    params.append(`where[${field}][in]`, unique.join(","));
  }
}

/**
 * Add treatment filters to query params
 */
function addTreatmentFilters(
  params: URLSearchParams,
  treatments?: Treatment[],
): void {
  if (!treatments || treatments.length === 0) return;
  addArrayFilterParam(
    params,
    "treatment_types",
    treatments,
    CMS_TREATMENT_TYPES,
  );
}

/**
 * Add service filters to query params
 */
function addServiceFilters(
  params: URLSearchParams,
  services?: Service[],
): void {
  if (!services || services.length === 0) return;
  addArrayFilterParam(
    params,
    "supporting_services",
    services,
    CMS_SUPPORTING_SERVICES,
  );
}

/**
 * Add language filters to query params
 */
function addLanguageFilters(
  params: URLSearchParams,
  languages?: string[],
): void {
  if (!languages || languages.length === 0) return;
  const cmsLanguages = languages
    .map((lang) => mapLanguageToCms(lang as any))
    .filter((l): l is string => Boolean(l));
  if (cmsLanguages.length === 0) return;

  if (cmsLanguages.length === 1) {
    params.append("where[languages][contains]", cmsLanguages[0]!);
  } else {
    params.append("where[languages][in]", cmsLanguages.join(","));
  }
}

/**
 * Add condition filters to query params
 */
function addConditionFilters(
  params: URLSearchParams,
  conditions?: Condition[],
): void {
  if (!conditions || conditions.length === 0) return;
  addArrayFilterParam(
    params,
    "specialties_conditions",
    conditions,
    CMS_CONDITIONS,
  );
}

/**
 * Add patient group filters to query params
 */
function addPatientGroupFilters(
  params: URLSearchParams,
  patientGroups?: PatientGroup[],
): void {
  if (!patientGroups || patientGroups.length === 0) return;
  addArrayFilterParam(
    params,
    "patient_groups",
    patientGroups,
    CMS_PATIENT_GROUPS,
  );
}

/**
 * Add consultation type filters to query params
 */
function addConsultationTypeFilters(
  params: URLSearchParams,
  consultationTypes?: ConsultationType[],
): void {
  if (!consultationTypes || consultationTypes.length === 0) return;
  addArrayFilterParam(
    params,
    "consultation_types",
    consultationTypes,
    CMS_CONSULTATION_TYPES,
  );
}

/**
 * Add accessibility filters to query params
 */
function addAccessibilityFilters(
  params: URLSearchParams,
  accessibilities?: Accessibility[],
): void {
  if (!accessibilities || accessibilities.length === 0) return;
  addArrayFilterParam(
    params,
    "accessibility",
    accessibilities,
    CMS_ACCESSIBILITY,
  );
}

/**
 * Add employee benefit filters to query params
 */
function addEmployeeBenefitFilters(
  params: URLSearchParams,
  employeeBenefits?: EmployeeBenefit[],
): void {
  if (!employeeBenefits || employeeBenefits.length === 0) return;
  const unique = Array.from(
    new Set(employeeBenefits.filter((b) => CMS_EMPLOYEE_BENEFITS.includes(b))),
  );
  if (unique.length === 0) return;

  if (unique.length === 1) {
    params.append("where[in_network_employee_benefits][contains]", unique[0]!);
  } else {
    params.append("where[in_network_employee_benefits][in]", unique.join(","));
  }
}

/**
 * Add financier filters to query params
 */
function addFinancierFilters(
  params: URLSearchParams,
  financiers?: Financier[],
): void {
  if (!financiers || financiers.length === 0) return;
  const unique = Array.from(
    new Set(financiers.filter((f) => CMS_FINANCING_OPTIONS.includes(f))),
  );
  if (unique.length === 0) return;

  if (unique.length === 1) {
    params.append("where[financing_options][contains]", unique[0]!);
  } else {
    params.append("where[financing_options][in]", unique.join(","));
  }
}

/**
 * Add basic filters (search, location, clinic type, booleans) to query params
 */
function addBasicFilters(
  params: URLSearchParams,
  filters: ClinicsFilters,
): void {
  if (filters.search) {
    params.append("where[name][contains]", filters.search);
  }

  if (filters.city) {
    params.append("where[city][contains]", filters.city);
  }

  if (filters.country) {
    params.append("where[country][equals]", filters.country);
  }

  if (filters.clinicType && CMS_CLINIC_TYPES.includes(filters.clinicType)) {
    params.append("where[type][equals]", filters.clinicType);
  }

  if (typeof filters.isFirstConsultationFree === "boolean") {
    params.append(
      "where[free_first_consult][equals]",
      String(filters.isFirstConsultationFree),
    );
  }

  if (typeof filters.insuranceAccepted === "boolean") {
    params.append(
      "where[insurance_accepted][equals]",
      String(filters.insuranceAccepted),
    );
  }

  if (filters.hasReportedAnnualIvfData) {
    params.append("where[reported_to][exists]", "true");
  }
}

/**
 * Build query parameters for CMS API from filters
 * This function orchestrates all filter parameter builders
 */
export function buildFiltersQueryParams(filters: ClinicsFilters): string {
  const params = new URLSearchParams();

  addBasicFilters(params, filters);
  addTreatmentFilters(params, filters.treatments);
  addServiceFilters(params, filters.services);
  addLanguageFilters(params, filters.languages);
  addConditionFilters(params, filters.conditions);
  addPatientGroupFilters(params, filters.patientGroups);
  addConsultationTypeFilters(params, filters.consultationTypes);
  addAccessibilityFilters(params, filters.accessibilities);
  addEmployeeBenefitFilters(params, filters.employeeBenefits);
  addFinancierFilters(params, filters.financiers);

  return params.toString();
}
